import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Upload, Package, ShoppingCart, Users,
  MapPin, AlertTriangle, Tags, Settings as SettingsIcon, LogOut, Droplet, UserCog, Menu, Repeat,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/imports", label: "Imports", icon: Upload, testId: "nav-imports", ownerOnly: true },
  { to: "/orders", label: "Orders", icon: ShoppingCart, testId: "nav-orders" },
  { to: "/producten-analyse", label: "Productanalyse", icon: Package, testId: "nav-products" },
  { to: "/klanten-analyse", label: "Klantanalyse", icon: Users, testId: "nav-customers" },
  { to: "/klantretentie", label: "Klantretentie", icon: Repeat, testId: "nav-cohort" },
  { to: "/regio-analyse", label: "Regioanalyse", icon: MapPin, testId: "nav-regions" },
  { to: "/controle", label: "Controle nodig", icon: AlertTriangle, testId: "nav-control" },
  { to: "/producten", label: "Producten & inkoopprijzen", icon: Tags, testId: "nav-product-prices", ownerOnly: true },
  { to: "/instellingen", label: "Instellingen", icon: SettingsIcon, testId: "nav-settings", ownerOnly: true },
  { to: "/gebruikers", label: "Gebruikers", icon: UserCog, testId: "nav-users", ownerOnly: true },
];

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const isOwner = user?.role === "owner";
  const isGuest = user?.role === "guest";
  const visibleItems = NAV_ITEMS.filter((it) => !it.ownerOnly || isOwner);

  const handleGuestLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex flex-col h-full bg-slate-900 text-slate-200">
      <div className="px-6 py-6 border-b border-slate-800">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-sky-500/20 border border-sky-500/30 flex items-center justify-center">
            <Droplet className="h-5 w-5 text-sky-300" />
          </div>
          <div>
            <p className="text-base font-semibold text-white tracking-tight">Lifjalla</p>
            <p className="text-xs text-slate-400 tracking-wider uppercase">Analytics</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {visibleItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testId}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-sky-500/15 text-white border border-sky-500/30"
                  : "text-slate-300 hover:bg-slate-800/80 hover:text-white border border-transparent"
              }`
            }
          >
            <item.icon className="h-4 w-4" />
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-slate-800">
        {isGuest ? (
          <>
            <div className="flex items-center gap-3 px-2 py-2">
              <div className="h-8 w-8 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
                <span className="text-amber-300 text-xs font-semibold">G</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">Gast-modus</p>
                <p className="text-xs text-slate-400 truncate">Niet aangemeld</p>
              </div>
            </div>
            <button
              onClick={handleGuestLogin}
              data-testid="sidebar-login-btn"
              className="w-full flex items-center gap-2 px-3 py-2 mt-2 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4 rotate-180" />
              Aanmelden
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 px-2 py-2">
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-sky-500/20 border border-sky-500/30" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="w-full flex items-center gap-2 px-3 py-2 mt-2 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Afmelden
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Sidebar() {
  return (
    <aside
      className="hidden md:flex md:flex-col w-64 fixed inset-y-0 left-0 z-30"
      data-testid="sidebar"
    >
      <SidebarContent />
    </aside>
  );
}

export function MobileTopbar() {
  const [open, setOpen] = useState(false);
  return (
    <header
      className="md:hidden sticky top-0 z-40 bg-slate-900 text-white flex items-center justify-between px-4 h-14 border-b border-slate-800"
      data-testid="mobile-topbar"
    >
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-md bg-sky-500/20 border border-sky-500/30 flex items-center justify-center">
          <Droplet className="h-4 w-4 text-sky-300" />
        </div>
        <div className="leading-tight">
          <p className="text-sm font-semibold tracking-tight">Lifjalla</p>
          <p className="text-[10px] text-slate-400 tracking-wider uppercase">Analytics</p>
        </div>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            data-testid="mobile-menu-btn"
            aria-label="Menu openen"
            className="h-10 w-10 rounded-md hover:bg-slate-800 flex items-center justify-center"
          >
            <Menu className="h-5 w-5" />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 w-72 max-w-[80vw] bg-slate-900 border-slate-800 text-slate-200">
          <SidebarContent onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
    </header>
  );
}
